import { useCallback, useMemo } from 'react';
import { ref, uploadBytes } from 'firebase/storage';
import useUserRoles from 'hooks/useUserRoles';
import useMultipartMutation from './useMultipartMutation';
import { storage } from '../../firebase';

interface SendNoteParams {
    agentId?: string;
    customerId?: string;
    inboxId?: string;
    opportunityId?: string;
    note?: string;
    files?: File[];
}

const buildFormData = (values: Record<string, unknown>) => {
    const data = new FormData();
    Object.keys(values).forEach(
        (key) => {
            const value = values[key];
            if (Array.isArray(value)) {
                value.forEach((item) => data.append(key, String(item)));
            } else if (value !== undefined && value !== null) {
                data.append(key, String(value));
            }
        },
    );
    return data;
};

const getUniqueFilename = (filename: string) => {
    const dotIndex = filename.lastIndexOf('.');
    const hasExtension = dotIndex > 0;
    const basename = hasExtension ? filename.slice(0, dotIndex) : filename;
    const extension = hasExtension ? filename.slice(dotIndex) : '';
    const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    return `${basename}-${uniqueSuffix}${extension}`;
};

const useSendNote = () => {
    const {
        mutate: hookMutate,
        mutateAsync: hookMutateAsync,
        ...rest
    } = useMultipartMutation({
        method: 'POST',
    });

    const { user: userData } = useUserRoles();

    const uploadFiles = useCallback(
        async ({
            agentId = '',
            customerId = '',
            files = [],
        }: Pick<SendNoteParams, 'agentId' | 'customerId' | 'files'>): Promise<string[]> => {
            if (!files.length) {
                return [];
            }

            const createdBy = JSON.stringify(userData || {});
            const attachedFileUrls = await Promise.all(
                files.map(async (file) => {
                    const uniqueFilename = getUniqueFilename(file.name);
                    const destination = `agents/${agentId}/customers/${customerId}/${uniqueFilename}`;
                    const fileRef = ref(storage, destination);
                    await uploadBytes(fileRef, file, {
                        contentType: file.type,
                        customMetadata: {
                            originalName: file.name,
                            createdBy,
                        },
                    });
                    return destination;
                }),
            );

            return attachedFileUrls;
        },
        [userData],
    );

    const buildBody = useCallback(
        async ({
            agentId,
            customerId,
            inboxId,
            opportunityId,
            note,
            files = [],
        }: SendNoteParams): Promise<FormData> => {
            const attachedFileUrls = await uploadFiles({
                agentId,
                customerId,
                files,
            });
            return buildFormData({
                agentId,
                customerId,
                inboxId,
                opportunityId,
                text: note || '',
                attachedFileUrls,
            });
        },
        [uploadFiles],
    );

    const mutate = useCallback(
        ({
            agentId,
            customerId,
            inboxId,
            opportunityId,
            note,
            files,
        }: SendNoteParams) => {
            const executeMutation = async () => {
                const body = await buildBody({
                    agentId,
                    customerId,
                    inboxId,
                    opportunityId,
                    note,
                    files,
                });
                hookMutate({
                    pathname: '/send-note',
                    body,
                });
            };

            executeMutation();
        },
        [buildBody, hookMutate],
    );

    const mutateAsync = useCallback(
        async ({
            agentId,
            customerId,
            inboxId,
            opportunityId,
            note,
            files,
        }: SendNoteParams) => hookMutateAsync({
            pathname: '/send-note',
            body: await buildBody({
                agentId,
                customerId,
                inboxId,
                opportunityId,
                note,
                files,
            }),
        }),
        [buildBody, hookMutateAsync],
    );

    return useMemo(() => ({ mutate, mutateAsync, ...rest }), [mutate, mutateAsync, rest]);
};

export default useSendNote;
